import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, setSessionToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CircleNotch } from "@phosphor-icons/react";
import { STATECRAFT_LOGO } from "@/lib/brand";

// Module-scope guard survives React StrictMode remounts and any component-tree churn.
// Prevents a second /auth/session exchange with the same (single-use) session_id.
let _sessionExchangeInFlight = false;
let _lastExchangedSessionId = null;

export default function AuthCallback() {
    const navigate = useNavigate();
    const { setUser } = useAuth();
    const hasProcessed = useRef(false);

    useEffect(() => {
        if (hasProcessed.current) return;
        hasProcessed.current = true;

        const hash = window.location.hash;
        const match = hash.match(/session_id=([^&]+)/);
        if (!match) {
            navigate("/", { replace: true });
            return;
        }
        const sessionId = match[1];

        // Module-scope de-dup — protects against StrictMode remounts.
        if (_sessionExchangeInFlight || _lastExchangedSessionId === sessionId) {
            return;
        }
        _sessionExchangeInFlight = true;
        _lastExchangedSessionId = sessionId;

        (async () => {
            try {
                const { data } = await api.post("/auth/session", { session_id: sessionId });
                // Persist Bearer token — required because the platform ingress strips
                // Access-Control-Allow-Origin to '*', which blocks cross-origin cookies.
                if (data?.session_token) setSessionToken(data.session_token);
                // Push user directly into context so ProtectedRoute doesn't need to
                // wait for a /auth/me round-trip — this eliminates the landing-page flicker.
                setUser(data);
                window.history.replaceState(null, "", "/dashboard");
                navigate("/dashboard", { replace: true });
            } catch (e) {
                console.error("Sign-in exchange failed:", e?.response?.status, e?.response?.data || e?.message);
                navigate("/", { replace: true });
            } finally {
                _sessionExchangeInFlight = false;
            }
        })();
    }, [navigate, setUser]);

    return (
        <div className="min-h-screen grid place-items-center bg-white text-slate-700">
            <div className="flex flex-col items-center gap-5" data-testid="auth-callback-loader">
                <img src={STATECRAFT_LOGO} alt="Statecraft" className="h-14 w-auto" />
                <CircleNotch size={36} className="animate-spin text-[#1E3A8A]" />
                <div className="text-sm text-slate-500">Signing you in…</div>
            </div>
        </div>
    );
}
